import {Button} from "@/components/ui/button.tsx";
import {Moon, Plus, Search, Sun, X} from "lucide-react";
import {useEffect, useState} from "react";
import {cities, type City} from "@/constants/cities.ts";
import {useTimeZone} from "@/hooks/useTimeZone.tsx";
import {convertTZ} from "@/functions/timeZone.ts";
import {useCitiesStorage} from "@/hooks/useCitiesStorage.ts";
import {Clock} from "@/class/Clock.ts";
import {useFormat} from "@/hooks/useFormat.tsx";
import {clsx} from "clsx";
import {toast} from "sonner";
import {KEY, useCurrentCity} from "@/hooks/useCurrentCity.tsx";
import {motion} from "motion/react"

interface CardProps {
    city: City
    elTimeZone?: string
}

interface AddCityProps {
    closeCity: (s: boolean) => void
}

interface ListProps {
    close: () => void
    city: City
}

/**
 * Composant principal affichant la liste des villes sélectionnées et le bouton pour ouvrir l'interface d'ajout.
 *
 * Gère:
 * - l'état d'ouverture du panneau d'ajout (`addCity`)
 * - la récupération des villes courantes depuis le stockage local via `useCitiesStorage`
 * - l'ajout d'une classe `no-scroll` sur le body quand le panneau d'ajout est ouvert
 */
export function Cities() {
    const [addCity, setAddCity] = useState(false)
    const { get } = useCitiesStorage(KEY)
    const registeredCities = get()
    const { currentCity } = useCurrentCity()

    useEffect(() => {
        if (addCity) document.body.classList.add("no-scroll")
        else document.body.classList.remove("no-scroll")
    }, [addCity]);

    return <div className={clsx(registeredCities && "border-b", "w-full")}>
        <div className="flex items-center justify-between p-8">
            <p className="text-[80px] font-bold max-[825px]:text-[50px] max-[560px]:text-[22px]">
                {currentCity}
            </p>
            <Button
                variant="ghost"
                className="cursor-pointer text-[18px] max-[450px]:text-[14px]"
                onClick={() => setAddCity(prev => !prev)}
            >
                Ajouter une ville
                <Plus className="shrink-0"/>
            </Button>
        </div>
        {addCity && <AddCity closeCity={setAddCity} />}
        <div className="grid grid-cols-3 justify-center gap-4 mb-8 mx-2 max-[1060px]:grid-cols-2 max-[815px]:grid-cols-1">
            {
                registeredCities &&
                    <>
                        {registeredCities.map(city => (
                            <Card city={city} />
                        ))}
                    </>
            }
        </div>
    </div>
}

/**
 * Panneau d'ajout de ville affiché en bas de l'écran.
 *
 * Props:
 * - closeCity: fonction pour fermer le panneau.
 *
 * Gère:
 * - l'état de la recherche (`search`)
 * - affichage de la liste filtrée des villes
 */
function AddCity({ closeCity }: AddCityProps) {
    const close = () => closeCity(false)
    const [search, setSearch] = useState("")

    const motionProperty = {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
    }

    return <motion.div {...motionProperty} className="w-full h-full fixed bottom-0 left-0 z-[999] bg-[#00000080]">
        <div className="m-3 flex items-center justify-end">
            <X size={30} className="shrink-0 cursor-pointer text-[20px] opacity-70 duration-300 hover:opacity-100 text-white" onClick={close} />
        </div>
        <div className="bg-[#e8e8e8] dark:bg-(--background) flex items-center flex-col overflow-auto" style={{height: "calc(100% - 55px)"}}>
            <div className="mt-6 mb-3">
                <div className="relative w-95 max-[540px]:w-80 flex flex-col">
                    <Search className="shrink-0 absolute top-[12px] left-2 text-primary" />
                    <input
                        type="search"
                        className="px-10 py-3 outline-none border-0 border-b text-[16px]"
                        placeholder="Rechercher..."
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                    />
                </div>
            </div>
            <div className="w-120 max-[535px]:w-80 mt-3">
                <ul className="flex flex-col gap-4">
                    {cities.map(city => {
                        if (search) return
                        return <List city={city} close={close} />
                    })}
                    {[...cities].filter(c => c.name.toLowerCase().includes(search.toLowerCase())).map(s => (
                        <List city={s} close={close} />
                    ))}
                </ul>
            </div>
        </div>
    </motion.div>
}

/**
 * Élément de liste représentant une ville dans le panneau d'ajout.
 *
 * Props:
 * - close: fonction pour fermer le panneau après sélection
 * - city: données de la ville sélectionnable
 *
 * Comportement:
 * - change le fuseau horaire via `useTimeZone`
 * - sauvegarde la ville choisie dans le stockage local via `useCitiesStorage`
 */
function List({ close, city }: ListProps) {
    const { changeTimeZone } = useTimeZone()
    const { changeCurrentCity } = useCurrentCity()
    const { set } = useCitiesStorage(KEY)

    const handleClick = () => {
        changeTimeZone(city.timeZone)
        changeCurrentCity(city.name)
        set(city)
        close()
    }

    return <li
        className="bg-card p-4 rounded-2xl hover:opacity-80 transform hover:scale-[1.05] cursor-pointer flex items-center justify-between transition-all duration-300"
        onClick={handleClick}
    >
        <p className="text-[20px] font-bold">{city.name}</p>
        <p className="font-semibold text-[16px]">{convertTZ(city.timeZone)}</p>
    </li>
}

/**
 * Carte affichant l'heure d'une ville.
 *
 * Props:
 * - city: objet City contenant `name` et `timeZone`
 *
 * Fonctionnalités:
 * - utilise `Clock` pour récupérer l'état horaire (heures, minutes, suffix, isDay)
 * - met à jour `cityDay` et `isDay` à intervalle régulier (ici 100ms)
 * - au clic change le fuseau horaire global et affiche une notification
 * - gère l'affichage selon le format (`useFormat`)
 * - suppression en utilisant le menu contextuel pour afficher le bouton d'effacement
 */
function Card({ city }: CardProps) {
    const { name, timeZone } = city
    const { format } = useFormat()
    const { changeTimeZone } = useTimeZone()
    const [cityDay, setCityDay] = useState(new Clock(format, timeZone).dayFormat)
    const [isDay, setIsDay] = useState(new Clock(format, timeZone).isDay)
    const { currentCity, changeCurrentCity } = useCurrentCity()
    const [showDel, setShowDel] = useState(false)
    const { remove } = useCitiesStorage()

    useEffect(() => {
        const interval = setInterval(() => {
            setCityDay(new Clock(format, timeZone).dayFormat)
            setIsDay(new Clock(format, timeZone).isDay)
        }, 100)
        return () => clearInterval(interval)
    }, [format]);

    const handleClick = () => {
        if (currentCity.toLowerCase() === city.name.toLowerCase()) return
        changeTimeZone(timeZone)
        changeCurrentCity(city.name)
        toast.success("Heure changée !")
    }

    const handleRemove = (e: MouseEvent) => {
        e.stopPropagation()
        setShowDel(false)
        remove(city.name)
        toast.warning(`${city.name} sera retirer de la liste à la prochaine visite`)
    }

    return <div
        className="relative flex flex-col gap-16 max-[555px]:gap-8 bg-transparent dark:bg-card border px-8 py-5 rounded-2xl cursor-pointer hover:border-primary transition duration-200"
        onClick={handleClick}
        onContextMenu={(e) => {
            e.preventDefault()
            setShowDel(prev => !prev)
        }}
    >
        {/*Bouton pour supprimer la ville de la liste*/}
        {showDel &&
            <div className="absolute top-0 left-0 w-full h-full bg-card rounded-2xl flex items-center justify-center" onClick={() => setShowDel(false)}>
                <Button
                    variant="destructive"
                    className="cursor-pointer scale-120 hover:scale-140 p-4"
                    onClick={handleRemove}
                >
                    Supprimer
                </Button>
            </div>
        }
        <div className="flex items-center justify-between text-[18px]">
            <p className="font-bold">{name}</p>
            <p className="text-primary">{convertTZ(timeZone)}</p>
        </div>
        <div className="flex items-center justify-between">
            <p className="text-[45px] font-bold">{cityDay.hours}:{cityDay.minutes} {format === "en-EN" && <span className="text-[16px]">{cityDay.suffix}</span>} </p>
            <div className="flex gap-2">
                {
                    isDay ? <Sun className="shrink-0 text-primary"/> : <Moon className="shrink-0 text-primary"/>
                }
                <p className="text-primary text-[18px] font-semibold">{isDay ? "Day" : "Moon"}</p>
            </div>
        </div>
    </div>
}